// Attach PDFs/photos (e.g. quotes from the old system) to a task.
import { useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { FileText, Loader2, Paperclip, Trash2 } from "lucide-react";

const viewFile = async (id) => {
  try {
    const res = await api.get(`/timeline/files/${id}/download`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    window.open(url, "_blank");
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch {
    toast.error("Could not open file");
  }
};

export const ActivityAttachments = ({ activityId }) => {
  const qc = useQueryClient();
  const fileRef = useRef(null);
  const { data: files = [] } = useQuery({
    queryKey: ["activity-files", activityId],
    queryFn: async () => (await api.get(`/timeline/files?activity_id=${activityId}`)).data,
    enabled: !!activityId,
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ["activity-files", activityId] });
  const upload = useMutation({
    mutationFn: async (fileList) => {
      for (const f of fileList) {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("activity_id", activityId);
        await api.post("/timeline/files", fd);
      }
    },
    onSuccess: () => { toast.success("Attached"); refresh(); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Upload failed"),
  });
  const del = useMutation({
    mutationFn: async (fid) => api.delete(`/timeline/files/${fid}`),
    onSuccess: refresh,
  });

  return (
    <div data-testid="activity-attachments">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Attachments — old quotes, PDFs, photos</span>
        <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={upload.isPending}
          className="rounded-sm h-8" data-testid="activity-upload-btn">
          {upload.isPending ? <Loader2 size={12} className="animate-spin mr-1.5" /> : <Paperclip size={12} className="mr-1.5" />} Upload PDF / photo
        </Button>
        <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.heic" multiple className="hidden"
          onChange={(e) => e.target.files?.length && upload.mutate(Array.from(e.target.files))}
          data-testid="activity-file-input" />
      </div>
      {files.length > 0 && (
        <div className="mt-2 border border-slate-200 rounded-sm divide-y divide-slate-100">
          {files.map((f) => (
            <div key={f.id} className="flex items-center gap-2 px-2.5 py-1.5 text-xs" data-testid={`activity-file-${f.id}`}>
              <FileText size={13} className="text-slate-400 shrink-0" />
              <button type="button" onClick={() => viewFile(f.id)}
                className="font-medium text-slate-800 hover:text-cyan-700 hover:underline truncate"
                data-testid={`activity-file-open-${f.id}`}>
                {f.label || f.filename}
              </button>
              <span className="text-slate-400 shrink-0">{Math.round((f.size || 0) / 1024)} KB</span>
              <button type="button" onClick={() => window.confirm("Remove this attachment?") && del.mutate(f.id)}
                className="ml-auto text-slate-300 hover:text-rose-600" data-testid={`activity-file-delete-${f.id}`}>
                <Trash2 size={12} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ActivityAttachments;
